import React from "react";
import Scrollchor from "react-scrollchor";
import Link from "gatsby-link";
import FadeIn from "../components/atoms/FadeIn";
import { HideMobile } from "../components/blocks/Responsive";
import styled, { keyframes } from "styled-components";
import Helmet from "react-helmet";
import { P, H3, H4 } from "../components/typography";
import Spacer from "../components/blocks/Spacer";
import Pad from "../components/blocks/Pad";
import NavBar from "../components/NavBar";
import Emphasis from "../components/atoms/Emphasis";
import { InternalButton } from "../components/atoms/Button";
import Section from "../components/blocks/Section";
import ContentSection from "../components/blocks/ContentSection";
import Timeline from "../components/Timeline";
import { getGraphNode } from "../selectors";
import WhatIf from "../components/WhatIf";
import TriangleBackground from "../components/blocks/TriangleBackground";
import Background from "../components/blocks/Background";
import favicon from "../../assets/logofavcon@2x.png";
import hillImg from "../../assets/hill-teal.png";
import caretWhiteImg from "../../assets/caret-white.png";
import foamHandImg from "../../assets/foam-hand.png";

const DEFAULT_META_DESCRIPTION =
  "Biotechnology company pioneering reversible male contraception. Our products, NES/T and ADAM, are daily and long-lasting options in clinical testing today.";

const TimelineQuote = H3.extend`
  padding-top: 4rem;
  display: flex;
  justify-content: center;
  text-align: center;
  font-weight: 400;
`;

const ProductSplitContainer = styled.div`
  display: flex;
  width: 100%;
  border-top: none;

  ${props => props.theme.media.tablet`
    flex-direction: column;
  `};
`;

const ProductPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 2rem;
  gap: 3rem;
  background-color: ${props => props.theme.colors.white};

  ${props => props.theme.media.mobile`
    justify-content: center;
    padding-top: 1rem;
    padding-bottom: 0;
    padding-left: 1rem;
    padding-right: 1rem;
    gap: 1rem;
  `};
`;

const ProductPanelLeft = ProductPanel.extend`
  justify-content: flex-end;
  border-right: none;

  ${props => props.theme.media.tablet`
    border-right: none;
    border-bottom: none;
  `};
`;

const ProductPanelRight = ProductPanel.extend`
  justify-content: flex-start;
`;

const ProductTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 3rem;

  ${props => props.theme.media.mobile`
    text-align: center;
    align-items: center;
    padding: 0 1rem;
  `};
`;

const ProductTextBlockRight = ProductTextBlock.extend`
  text-align: right;
  align-items: flex-end;

  ${props => props.theme.media.mobile`
    text-align: center;
    align-items: center;
  `};
`;

const ProductImageWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  min-width: 250px;

  ${props => props.theme.media.mobile`
    min-width: 160px;
  `};
`;

const ProductImage = styled.img`
  height: 396px;
  max-width: 100%;
  object-fit: contain;
  object-position: bottom;
  margin-bottom: -0.75rem;

  ${props => props.theme.media.mobile`
    height: 220px;
  `};
`;

const ProductName = styled.h2`
  font-family: "Palatino Linotype", "Book Antiqua", Palatino, serif;
  font-size: 1.875rem;
  font-weight: bold;
  margin: 0 0 0.5rem;
`;

const ProductType = styled.p`
  font-family: "Avenir Next", "Avenir", Helvetica, Arial, sans-serif;
  font-size: 1.4rem;
  margin: 0 0 0.75rem;
  max-width: 280px;
`;

const LearnMore = styled.span`
  font-family: "Avenir Next", "Avenir", Helvetica, Arial, sans-serif;
  font-size: 1.2rem;
  border-bottom: 1px solid currentColor;
  padding-bottom: 2px;
  margin-top: 0.5rem;
  display: inline-block;
`;

const shimmer = keyframes`
  0% {
    background-position: 100% center;
  }
  100% {
    background-position: 0% center;
  }
`;


const NewsBanner = styled.a`
  display: block;
  line-height: 40px;
  background: linear-gradient(
    105deg,
    #43bcba 0%,
    #43bcba 40%,
    #6dd4d2 46%,
    #8ae0de 50%,
    #6dd4d2 54%,
    #43bcba 60%,
    #43bcba 100%
  );
  background-size: 300% 100%;
  background-position: 100% center;
  animation: ${shimmer} 1.5s ease-in-out 0.5s 1 forwards;
  color: white;
  text-decoration: none;
  font-size: 0.9rem;
  padding: 0 1rem;
  text-align: center;

  strong {
    margin-right: 0.3em;
  }

  &:hover {
    background-color: #3aa8a6;
  }

  ${props => props.theme.media.mobile`
    font-size: 1.04rem;
    line-height: 1.4;
    padding: 0.5rem 1rem;
    line-height: 1.4;
  `};
`;

const HillArrow = () => {
  return (
    <div
      style={{
        position: "absolute",
        bottom: -6,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center"
      }}
    >
      <Scrollchor
        to="future"
        className="nav-link"
        style={{ position: "relative", cursor: "pointer" }}
        animate={{ offset: -60, duration: 600 }}
      >
        <img src={hillImg} style={{ height: 40, width: 146 }} />
        <img
          style={{ position: "absolute", top: 16, left: 64 }}
          src={caretWhiteImg}
        />
      </Scrollchor>
    </div>
  );
};

export default class Index extends React.Component {
  render() {
    const content = getGraphNode(this.props.data, "HomePage");
    const metaDescription = content.metaDescription || DEFAULT_META_DESCRIPTION;
    return (
      <div>
        <Helmet
          title="Contraline"
          meta={[
            {
              name: "description",
              content: metaDescription
            }
          ]}
        >
          <link rel="icon" type="image/png" href={favicon} />
        </Helmet>
        <NewsBanner href={(content.newsBannerUrl) || "https://www.contraline.com/news/c6LExhJDANlbUsl3z4MXiJY"}>
          <span>{(content.newsBannerText && content.newsBannerText.newsBannerText) || <span><strong>Announcing:</strong> Contraline Exclusively Licenses NES/T Male Contraceptive Gel →</span>}</span>
        </NewsBanner>
        <TriangleBackground>
          <NavBar color="dark" />
          <Section
            padLarge
            justify="center"
            align="center"
            vPad={false}
            style={{ flex: 1 }}
          >
            <WhatIf list={content.whatIfText.whatIfText.split("|")} />
            <HideMobile>
              <Spacer span={2} />
            </HideMobile>
          </Section>
          <HillArrow />
        </TriangleBackground>

        <div id="future" />
        <Background color="teal">
          <Pad v={6}>
            <ContentSection
              color="transparent"
              fontColor="white"
              titleText={content.futureTitle}
              bodyText={content.futureBodyText.futureBodyText.split("|")}
            />
          </Pad>
        </Background>
        <Background color="greyDient">
          <Section padLarge={false}>
            <Pad mv={1}>
              <Emphasis>
                <H4>
                  {content.timelineTitle}
                </H4>
              </Emphasis>
            </Pad>
            <Section>
              <Timeline entries={content.timelineEntries.entries} />
            </Section>
            <Spacer span={1} />
            <FadeIn>
              <TimelineQuote>
                {content.timelineConclusion}
              </TimelineQuote>
              <Spacer span={2} />
              <div
                style={{
                  textAlign: "center",
                  width: "100%"
                }}
              >
                <InternalButton to="/participate">
                  {content.timelineCtaText || "Interested in the NES/T Trial?"}
                </InternalButton>
              </div>
            </FadeIn>
            <Spacer span={8} />
          </Section>
        </Background>

        <div>
          <H3 align="center" style={{ marginBottom: 0, marginTop: "6rem" }}>
            {content.productTitle || "Finally, some new options for men."}
          </H3>
        <ProductSplitContainer>
          <ProductPanelLeft>
            <ProductImageWrap>
              <ProductImage style={{ transform: "scaleX(-1)" }} src={(content.nestImage && content.nestImage.responsiveSizes) ? content.nestImage.responsiveSizes.src : foamHandImg} alt={content.nestProductName || "NES/T"} />
            </ProductImageWrap>
            <ProductTextBlockRight>
              <ProductName>{content.nestProductName || "NES/T"}</ProductName>
              <ProductType>{(content.nestProductDescription && content.nestProductDescription.nestProductDescription) || "A daily topical gel that suppresses sperm in weeks and reverses when you stop."}</ProductType>
              <Link to="/nestorone" style={{ textDecoration: "none", color: "#43bcba" }}>
                <LearnMore>{content.nestProductLinkText || "← Learn more"}</LearnMore>
              </Link>
            </ProductTextBlockRight>
          </ProductPanelLeft>
          <ProductPanelRight>
            <ProductTextBlock>
              <ProductName>{content.adamName || "ADAM"}</ProductName>
              <ProductType>{(content.adamDescription && content.adamDescription.adamDescription) || "The world's first implant designed to provide men with long-lasting contraception."}</ProductType>
              <Link to="/product" style={{ textDecoration: "none", color: "#43bcba" }}>
                <LearnMore>{content.adamLinkText || "Learn more →"}</LearnMore>
              </Link>
            </ProductTextBlock>
            <ProductImageWrap>
              <ProductImage src={content.glove.responsiveSizes.src} alt={content.adamName || "ADAM"} />
            </ProductImageWrap>
          </ProductPanelRight>
        </ProductSplitContainer>
        </div>
      </div>
    );
  }
}

export const pageQuery = graphql`
  query HomePageQuery {
    allContentfulHomePage {
      edges {
        node {
          id
          metaDescription
          futureTitle
          futureBodyText {
            futureBodyText
          }
          whatIfText {
            whatIfText
          }
          timelineTitle
          timelineEntries {
            entries {
              src
              date
              event
              bold
            }
          }
          timelineConclusion
          productTitle
          productBodyText {
            productBodyText
          }
          productLink
          glove {
            responsiveSizes(maxWidth: 600) {
              src
            }
          }
          newsBannerText {
            newsBannerText
          }
          newsBannerUrl
          adamName
          adamDescription {
            adamDescription
          }
          adamLinkText
          nestProductName
          nestProductDescription {
            nestProductDescription
          }
          nestProductLinkText
          timelineCtaText
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/pages/index.js